import { useState } from "react";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import AutoLayout from "jadro-pico-app/Components/UI/Layouts/AutoLayout/AutoLayout";
import {
  AutoLayoutAlignEnum,
  AutoLayoutDirectionEnum,
  AutoLayoutResizingEnum,
} from "jadro-pico-app/Components/UI/Layouts/AutoLayout/AutoLayoutEnums";
import FontText from "jadro-pico-app/Components/UI/FontText/FontText";
import { FontSizeEnum, FontTypeEnum } from "jadro-pico-app/Components/UI/FontText/FontTextEnums";
import { ThemeColorEnum } from "jadro-pico-app/Components/UI/Themes/ThemeEnums";
import SectionCard from "jadro-pico-app/Components/UI/SectionCard/SectionCard";
import { SectionCardVariantEnum } from "jadro-pico-app/Components/UI/SectionCard/SectionCardVariantEnum";
import List from "jadro-pico-app/Components/UI/List/List";
import ListItem from "jadro-pico-app/Components/UI/List/ListItem/ListItem";
import { FilledButton, TextButton } from "jadro-pico-app/Components/UI/Button";
import TextField from "jadro-pico-app/Components/UI/TextField/TextField";
import FormField from "jadro-pico-app/Components/UI/FormField/FormField";
import Skeleton from "jadro-pico-app/Components/UI/Skeleton/Skeleton";
import { SkeletonShapeEnum } from "jadro-pico-app/Components/UI/Skeleton/SkeletonEnums";
import { useApiQuery } from "jadro-pico-app/Api/useApiQuery";
import { useDeferrableMutation } from "jadro-pico-app/Api/useDeferrableMutation";
import { showSnackbar } from "jadro-pico-app/Components/UI/Snackbar/showSnackbar";
import { useResource } from "jadro-pico-app/Resources/useResource";
import { ApiPaths } from "jadro-pico/api-endpoints/ApiPaths";
import type { ApiResponse } from "jadro-pico/dtos/common/ApiResponse";
import type { RacerProfileDto } from "jadro-pico/dtos/beerrally/RacerProfileDto";
import { RacerProfileResources } from "../../Resources/RacerProfileResources";
import { CommonResources } from "../../Resources/CommonResources";
import { QueryKeys } from "../../queryKeys";
import BackHeaderPage from "jadro-pico-app/Components/UI/Layouts/PageLayout/BackHeaderPage";

const PROFILE_URL = `${import.meta.env.VITE_PICO_API_URL}${ApiPaths.beerrally.profile.prefix}${ApiPaths.beerrally.profile.me}`;

type ProfileField = "team" | "licence" | "beer" | "raceClass";

interface RacerProfileSectionProps {
  onCollapse: () => void;
}

function RacerProfileSection({ onCollapse }: RacerProfileSectionProps) {
  const t = useResource(RacerProfileResources);
  const common = useResource(CommonResources);
  const [editingField, setEditingField] = useState<ProfileField | null>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useApiQuery<ApiResponse<RacerProfileDto>>({
    url: PROFILE_URL,
    queryKey: QueryKeys.racerProfile,
    retry: false,
  });

  const mutation = useDeferrableMutation<ApiResponse<unknown>, RacerProfileDto>({
    url: PROFILE_URL,
    method: "PUT",
    operationLabel: t.operationLabel,
  });

  const profile: RacerProfileDto = data?.data ?? {
    team: null,
    beer: null,
    raceClass: null,
    licence: null,
  };

  const fields: { key: ProfileField; label: string }[] = [
    { key: "team", label: t.fieldTeam },
    { key: "licence", label: t.fieldLicence },
    { key: "beer", label: t.fieldBeer },
    { key: "raceClass", label: t.fieldClass },
  ];

  const loadError = isError
    ? {
        headline: common.errorLoadHeadline,
        supporting: common.errorLoadSupporting,
        retry: { label: common.actionRetry, onClick: () => void refetch() },
      }
    : undefined;

  const listSkeleton = (
    <SectionCard variant={SectionCardVariantEnum.SURFACE}>
      <AutoLayout
        $direction={AutoLayoutDirectionEnum.VERTICAL}
        $horizontalResizing={AutoLayoutResizingEnum.FILL}
        $gap={20}
      >
        {fields.map((f) => (
          <AutoLayout
            key={f.key}
            $direction={AutoLayoutDirectionEnum.VERTICAL}
            $horizontalResizing={AutoLayoutResizingEnum.FILL}
            $gap={6}
          >
            <Skeleton shape={SkeletonShapeEnum.TEXT} width="35%" />
            <Skeleton shape={SkeletonShapeEnum.TEXT} width="55%" />
          </AutoLayout>
        ))}
      </AutoLayout>
    </SectionCard>
  );

  const openField = (key: ProfileField) => {
    setDraft(profile[key] ?? "");
    setError(null);
    setEditingField(key);
  };

  const save = async () => {
    if (!editingField) return;
    const value = draft.trim() || null;
    const payload: RacerProfileDto = { ...profile, [editingField]: value };
    try {
      await mutation.mutateAsync(payload);
      void queryClient.invalidateQueries({ queryKey: QueryKeys.racerProfile });
      showSnackbar(dispatch, t.saveSubmitted);
      setEditingField(null);
    } catch {
      setError(common.errorSave);
    }
  };

  if (editingField) {
    const field = fields.find((f) => f.key === editingField)!;
    return (
      <BackHeaderPage
        title={field.label}
        backLabel={common.actionBack}
        onBack={() => setEditingField(null)}
      >
        <SectionCard variant={SectionCardVariantEnum.SURFACE}>
          <FormField label={field.label}>
            <TextField value={draft} onChange={(e) => setDraft(e.target.value)} />
          </FormField>
          {error && (
            <FontText
              text={error}
              $fontSize={FontSizeEnum.SMALL}
              $fontType={FontTypeEnum.BODY}
              $color={ThemeColorEnum.ERROR}
            />
          )}
          <AutoLayout
            $direction={AutoLayoutDirectionEnum.HORIZONTAL}
            $align={AutoLayoutAlignEnum.RIGHT}
            $horizontalResizing={AutoLayoutResizingEnum.FILL}
            $gap={8}
          >
            <TextButton label={common.actionCancel} onClick={() => setEditingField(null)} />
            <FilledButton
              label={common.actionSave}
              onClick={() => void save()}
              disabled={mutation.isPending}
            />
          </AutoLayout>
        </SectionCard>
      </BackHeaderPage>
    );
  }

  return (
    <BackHeaderPage
      title={t.sectionTitle}
      backLabel={common.actionBack}
      onBack={onCollapse}
      isLoading={isLoading}
      error={loadError}
      loadingSkeleton={listSkeleton}
    >
      <SectionCard variant={SectionCardVariantEnum.SURFACE} flush>
        <List dividers>
          {fields.map((f) => (
            <ListItem
              key={f.key}
              headline={f.label}
              supportingText={profile[f.key] ?? common.stateNotSet}
              onClick={() => openField(f.key)}
            />
          ))}
        </List>
      </SectionCard>
    </BackHeaderPage>
  );
}

export default RacerProfileSection;
